import { useEffect, useLayoutEffect } from 'react';
import classNames from '@/common/utils/classNames';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import {
  formatGrowthIndicatorValue,
  getGrowthPathTeaser,
  growthIndicatorStatusLabel,
  useGrowthPath,
  type GrowthPathIndicator,
  type GrowthPathStep,
  type GrowthPathStepState,
} from '../apis/growthOpportunities';
import { DsBadge, DsButton, DsCard, ds } from '../designSystem';
import { useDoctorHomeContext } from '../hooks/useDoctorHomeContext';
import { useSheetDrawerProps } from '../hooks/doctorHomeSheetLayout';
import { useSheetRoute } from '../hooks/useSheetRoute';
import { useGrowthPathExpandedStore } from '../store/growthPathExpanded';
import { useGrowthPathStepSheetStore } from '../store/growthPathStepSheet';
import { dsFocusRing } from '../utils/a11y';
import { sendDoctorHomeEvent } from '../utils/analytics';
import { isQueryInitialLoading } from '../utils/doctorHomeQuery';
import { appendUserIdToUrl } from '../utils/iframeUrl';
import { DsCollapsible } from './dsCollapsible';
import { DsDrawer } from './DsDrawer';
import { ExpandChevronIcon, GrowthPathIcon, TimelineDoneIcon } from './icons';

const SANJE_PERFORMANCE_URL =
  'https://jahannama.paziresh24.com/my-performance/?utm_source=p24portal&utm_medium=internal-link&utm_campaign=sanje-my-performance';

const markerClass: Record<GrowthPathStepState, string> = {
  done: ds.timeline.done,
  active: ds.timeline.current,
  upcoming: ds.timeline.upcoming,
};

const connectorClass: Record<GrowthPathStepState, string> = {
  done: ds.timeline.lineDone,
  active: ds.timeline.lineCurrent,
  upcoming: ds.timeline.lineUpcoming,
};

const titleClass: Record<GrowthPathStepState, string> = {
  done: 'text-[13px] font-medium leading-6 text-slate-700',
  active: 'text-[13px] font-semibold leading-6 text-primary',
  upcoming: 'text-[13px] font-medium leading-6 text-slate-500',
};

const PANEL_ID = 'doctor-home-growth-path-panel';

const statusTone = (status?: GrowthPathIndicator['status']): 'success' | 'warning' | 'neutral' => {
  if (status === 'ok') return 'success';
  if (status === 'improve') return 'warning';
  return 'neutral';
};

const actionLabelFor = (step: GrowthPathStep) => {
  if (step.action_label) return step.action_label;
  const status = step.indicators.find(item => item.status)?.status;
  if (status === 'ok') return 'مشاهده جزئیات';
  return 'اصلاح این مورد';
};

export const FeedGrowthPathSection = ({ className }: { className?: string }) => {
  const user = useUserInfoStore(state => state.info);
  const userId = user?.id;
  const { isDoctor } = useDoctorHomeContext(user);
  const growthPath = useGrowthPath(isDoctor);
  const path = growthPath.data;
  const isLoading = isQueryInitialLoading(growthPath);
  const performanceSheet = useSheetRoute('stat-performance');
  const performanceDrawerProps = useSheetDrawerProps(performanceSheet);
  const stepSheet = useSheetRoute('growth-path-step');
  const isExpanded = useGrowthPathExpandedStore(state => state.isExpanded);
  const hydrated = useGrowthPathExpandedStore(state => state.hydrated);
  const hydrate = useGrowthPathExpandedStore(state => state.hydrate);
  const toggle = useGrowthPathExpandedStore(state => state.toggle);
  const setStep = useGrowthPathStepSheetStore(state => state.setStep);

  useLayoutEffect(() => {
    hydrate();
  }, [hydrate]);

  if (!isDoctor) return null;
  if (!isLoading && !path) return null;

  const openSanje = () => {
    sendDoctorHomeEvent(userId, 'stat_performance', {
      completed: path?.completed_count,
      total: path?.total_count,
    });
    performanceSheet.openSheet();
  };

  const openStepSheet = (step: GrowthPathStep, index: number) => {
    setStep(step);
    sendDoctorHomeEvent(userId, 'growth_path_step', {
      key: step.key,
      title: step.title,
      state: step.state,
      index,
    });
    stepSheet.openSheet(step.key ? { growth_step: step.key } : undefined);
  };

  const teaser = path ? getGrowthPathTeaser(path) : null;
  const subtitle = teaser?.complete
    ? 'کامل شد'
    : teaser
      ? `${teaser.remaining.toLocaleString('fa-IR')} قدم مانده`
      : isLoading
        ? 'در حال بارگذاری…'
        : '—';

  return (
    <section aria-label="مسیر رشد شما" className={className}>
      <DsCard padding="none" className="overflow-hidden">
        <button
          type="button"
          onClick={toggle}
          className={classNames(
            'flex w-full items-center gap-3 text-start',
            ds.layout.rowPadding,
            ds.motion.listRow,
            dsFocusRing,
          )}
          aria-expanded={hydrated ? isExpanded : true}
          aria-controls={PANEL_ID}
          aria-label={isExpanded ? 'بستن مسیر رشد' : 'باز کردن مسیر رشد'}
        >
          <span className={classNames('h-10 w-10', ds.icon.containerAction, ds.radius.inner)}>
            <GrowthPathIcon size="md" />
          </span>
          <span className="min-w-0 flex-1">
            <p className={ds.type.cardTitle}>مسیر رشد شما</p>
            <p className={classNames(ds.type.caption, 'mt-0.5')}>{subtitle}</p>
          </span>
          <ExpandChevronIcon
            size="sm"
            className={classNames('shrink-0 text-slate-300', ds.motion.chevron, isExpanded && 'rotate-180')}
          />
        </button>

        <DsCollapsible open={hydrated ? isExpanded : true} id={PANEL_ID}>
          <div className="border-t border-slate-100 px-3 pb-3 pt-2">
            {isLoading && !path ? (
              <div className="space-y-4" aria-busy="true" aria-label="در حال بارگذاری مسیر رشد">
                {[0, 1, 2].map(i => (
                  <div key={i} className="flex gap-3">
                    <div className="h-6 w-6 shrink-0 skeleton-shimmer rounded-full bg-slate-200" />
                    <div className="h-11 flex-1 skeleton-shimmer rounded-xl bg-slate-200" />
                  </div>
                ))}
              </div>
            ) : path ? (
              <>
                <ol>
                  {path.steps.map((step, index) => {
                    const isLast = index === path.steps.length - 1;
                    const isActive = step.state === 'active';

                    return (
                      <li key={step.key ?? `${step.state}-${index}`} aria-current={isActive ? 'step' : undefined}>
                        <button
                          type="button"
                          onClick={() => openStepSheet(step, index)}
                          className={classNames(
                            'relative flex min-h-11 w-full gap-3 text-start',
                            dsFocusRing,
                            ds.motion.press,
                          )}
                        >
                          <span className="relative flex w-6 shrink-0 flex-col items-center">
                            <span
                              className={classNames(
                                'relative z-[1] flex h-6 w-6 items-center justify-center rounded-full',
                                markerClass[step.state],
                              )}
                              aria-hidden
                            >
                              {step.state === 'done' && <TimelineDoneIcon />}
                              {step.state === 'active' && (
                                <span className="h-[7px] w-[7px] rotate-45 rounded-[1.5px] bg-white" />
                              )}
                            </span>
                            {!isLast && (
                              <span
                                className={classNames(
                                  'absolute left-1/2 top-6 bottom-0 w-0.5 -translate-x-1/2 translate-y-0.5',
                                  connectorClass[step.state],
                                )}
                                aria-hidden
                              />
                            )}
                          </span>

                          <span className={classNames('min-w-0 flex-1 pt-0.5', isLast ? 'pb-0.5' : 'pb-5')}>
                            <span className={classNames('block', titleClass[step.state])}>{step.title}</span>
                            {isActive && (
                              <span className="mt-2 inline-flex items-center rounded-full bg-primary/[0.08] px-2.5 py-0.5 text-[11px] font-semibold text-primary">
                                قدم فعلی شما
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
                <button
                  type="button"
                  onClick={openSanje}
                  className={classNames(ds.type.link, 'mt-3', dsFocusRing)}
                >
                  مشاهده عملکرد
                </button>
              </>
            ) : null}
          </div>
        </DsCollapsible>
      </DsCard>

      <DsDrawer {...performanceDrawerProps} title="عملکرد من" description="سنجه" fullHeight className="!p-0">
        {performanceSheet.open && (
          <iframe
            src={appendUserIdToUrl(SANJE_PERFORMANCE_URL, userId)}
            title="عملکرد من در سنجه"
            className="min-h-0 w-full flex-1 border-0"
          />
        )}
      </DsDrawer>
    </section>
  );
};

export const FeedGrowthPathSheets = () => {
  const user = useUserInfoStore(state => state.info);
  const userId = user?.id;
  const { isDoctor } = useDoctorHomeContext(user);
  const growthPath = useGrowthPath(isDoctor);
  const path = growthPath.data;
  const stepSheet = useSheetRoute('growth-path-step');
  const stepDrawerProps = useSheetDrawerProps(stepSheet);
  const actionSheet = useSheetRoute('growth-path-action');
  const actionDrawerProps = useSheetDrawerProps(actionSheet);
  const selectedStep = useGrowthPathStepSheetStore(state => state.step);
  const setStep = useGrowthPathStepSheetStore(state => state.setStep);

  useEffect(() => {
    if (!stepSheet.open || selectedStep || !path) return;
    const key =
      typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('growth_step') : null;
    const match = key ? path.steps.find(step => step.key === key) : null;
    if (match) setStep(match);
  }, [stepSheet.open, selectedStep, path, setStep]);

  if (!isDoctor) return null;

  const openActionSheet = () => {
    if (!selectedStep?.action_url) return;
    sendDoctorHomeEvent(userId, 'growth_path_action', {
      key: selectedStep.key,
      title: selectedStep.title,
      url: selectedStep.action_url,
    });
    actionSheet.openSheet();
  };

  const actionSrc = selectedStep?.action_url
    ? selectedStep.action_url.startsWith('/')
      ? selectedStep.action_url
      : appendUserIdToUrl(selectedStep.action_url, userId)
    : undefined;

  const primary = selectedStep?.indicators[0];
  const title = primary?.label ?? selectedStep?.title ?? 'مسیر رشد';
  const statusLabel = growthIndicatorStatusLabel(primary?.status);
  const ctaLabel = selectedStep ? actionLabelFor(selectedStep) : 'اصلاح این مورد';

  return (
    <>
    <DsDrawer {...stepDrawerProps} title={title} description={primary?.hint ?? selectedStep?.title}>
      <div className="flex flex-col">
        <div className="px-5 pb-5 pt-1">
          {statusLabel ? (
            <DsBadge tone={statusTone(primary?.status)} className="mb-3">
              {statusLabel}
            </DsBadge>
          ) : null}
          <h2 className="text-[17px] font-bold leading-7 text-slate-900">{title}</h2>
          {primary?.hint ? (
            <p className={classNames(ds.type.body, 'mt-2 leading-7 text-slate-600')}>{primary.hint}</p>
          ) : null}
          {primary?.value != null ? (
            <p className={classNames(ds.type.insightValueLg, 'mt-4')}>{formatGrowthIndicatorValue(primary)}</p>
          ) : null}
          {selectedStep?.checklist?.length ? (
            <ol className="mt-5 space-y-2.5">
              {selectedStep.checklist.map((item, index) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-500">
                    {(index + 1).toLocaleString('fa-IR')}
                  </span>
                  <span className={classNames(ds.type.body, 'leading-6 text-slate-700')}>{item}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
        {selectedStep?.action_url ? (
          <div className="border-t border-slate-100 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
            <DsButton variant="primary" className="h-12 w-full text-sm" onClick={openActionSheet}>
              {ctaLabel}
            </DsButton>
          </div>
        ) : null}
      </div>
    </DsDrawer>

    <DsDrawer
      {...actionDrawerProps}
      title={ctaLabel}
      description={title}
      fullHeight
      className="!p-0"
      level={1}
    >
      {actionSheet.open && actionSrc ? (
        <iframe src={actionSrc} title={ctaLabel} className="min-h-0 w-full flex-1 border-0" />
      ) : null}
    </DsDrawer>
    </>
  );
};
